import { ArrayUtils, StringUtils } from '../../utils'
import { gql, useLazyQuery } from '@apollo/client'
import { Button, Spin, notification } from 'antd'
import Title from 'antd/es/typography/Title'
import React, { JSX, useEffect, useState } from 'react'
 
import useDocumentTitle from '~/hooks/useDocumentTitle'
import config from '../../config'
import DownloadError from './DownloadError'
import DownloadPrimaryTable, { PrimaryTableType } from './DownloadTablePrimary'
import DownloadSecondaryTable from './DownloadTableSecondary'
import { DownloadWrapper } from './DownloadWrapper'
import { showErrorModal } from '../order-form/OrderFormUtils'

export interface DownloadEToursProps {
  disseminationKey: string
  missingEToursOnly: string
}

const GET_ORDER_GQL = gql`
  query GetOrderWellsFiles($where: OrderWhereInput) {
    findFirstOrderExt(where: $where) {
      Id
      Number
      Bills {
        Filename
        Source
        Path
        Size
      }
      WellIdentifiersOnOrders {
        RequestedUWI
        LicenseNumber
        WellIdentifier {
          Id
          OriginalUWI
          LicenseNumber
          File {
            Filename
            Size
            Source
            Path
            SubmissionId
            DocumentId
          }
        }
      }
    }
  }
`

const GET_ORDER_ON_ETOURS_GQL = gql`
  query GetWellsFiles($where: WellIdentifiersOnOrdersWhereInput) {
    findWellsOnOrderExt(where: $where) {
      Id
      Number
      WellIdentifiersOnOrders {
        RequestedUWI
        LicenseNumber
        WellIdentifier {
          Id
          OriginalUWI
          LicenseNumber
          File {
            Filename
            Size
            Source
            Path
            SubmissionId
            DocumentId
          }
        }
      }
    }
  }
`

const DOWNLOAD_TOKEN_GQL = gql`
  query DownloadToken(
    $order: Int!
    $missingEToursOnly: Boolean!
    $uwi: String
    $file: String
    $isOrderBill: Boolean
  ) {
    downloadToken(
      order: $order
      missingEToursOnly: $missingEToursOnly
      uwi: $uwi
      file: $file
      isOrderBill: $isOrderBill
    )
  }
`

const DownloadETours = (props: DownloadEToursProps): JSX.Element => {
  let token = ''
  try {
    const oauth = window.sessionStorage.getItem('oauth')
    const jwt = JSON.parse(oauth)
    token = jwt.access_token
  } catch (e) {
    console.error(e)
  }

  const [notify, contextHolder] = notification.useNotification()
  const { disseminationKey, missingEToursOnly } = props
  const [isNoData, setIsNoData] = useState<string>()
  const [getOrder, { loading: getOrderLoading, data: getOrderData, error: getOrderError }] =
    useLazyQuery(missingEToursOnly === 'true' ? GET_ORDER_ON_ETOURS_GQL : GET_ORDER_GQL, {
      context: {
        headers: {
          authorization: 'Bearer ' + token,
        },
      },
    })
  const [
    downloadToken,
    { loading: downloadTokenLoading, data: downloadTokenData, error: downloadTokenError },
  ] = useLazyQuery(DOWNLOAD_TOKEN_GQL, {
    context: {
      headers: {
        authorization: 'Bearer ' + token,
      },
    },
  })

  const getWellIdentifiersOnOrders = () => {
    if (!getOrderData) return []

    return missingEToursOnly === 'true'
      ? getOrderData.findWellsOnOrderExt?.WellIdentifiersOnOrders
      : getOrderData.findFirstOrderExt?.WellIdentifiersOnOrders
  }

  const getOrderNumber = () => {
    if (!getOrderData) return 0
    return missingEToursOnly === 'true'
      ? getOrderData.findWellsOnOrderExt?.Number
      : getOrderData.findFirstOrderExt?.Number
  }

  const getBills = () => {
    if (!getOrderData) return []
    return missingEToursOnly === 'true' ? [] : getOrderData.findFirstOrderExt?.Bills ?? []
  }

  useDocumentTitle('Tour Report Downloads')
 

  useEffect(() => {
    if (StringUtils.isEmpty(disseminationKey) || disseminationKey === 'auth') {
      setIsNoData('400-001')
      return
    }

    if (StringUtils.isEmpty(token)) {
      setIsNoData('401-001')
      return
    }

    getOrder({
      variables: {
        where: {
          DisseminationKey: {
            equals: disseminationKey,
          },
        },
      },
    })

    return () => {
      setIsNoData(null)
    }
  }, [])

  useEffect(() => {
    if (getOrderData == null) {
      return
    }
    setIsNoData(ArrayUtils.isEmpty(getWellIdentifiersOnOrders()) ? '404-002' : null)
  }, [getOrderData])

  useEffect(() => {
    if (downloadTokenData?.downloadToken == null) {
      return
    }
    const origin = config.downloadUri.startsWith('http') ? '' : window.location.origin
    const newWindow = window.open(origin + config.downloadUri + '/files/' + downloadTokenData.downloadToken)
    if (!newWindow) {
       showErrorModal('Please allow pop-ups for this site in your browser settings to continue.', 'Pop-up Blocked')
    }

  }, [downloadTokenData])

  useEffect(() => {
    const err = getOrderError ?? downloadTokenError
    if (err != null) {
      const msg = err.message ?? 'Unknown error.'
      if (msg.startsWith('40') || msg.startsWith('50')) {
        setIsNoData(msg)
        return
      }
      notify.error({
        message: 'Error!',
        description: err.message ?? 'Unknown error.',
      })
    }
  }, [getOrderError, downloadTokenError])

  const downloadHandler = (
    order: number,
    uwi?: string,
    file?: string,
    isOrderBill?: boolean
  ): void => {
    downloadToken({
      variables: {
        order,
        missingEToursOnly: missingEToursOnly === 'true',
        uwi,
        file,
        isOrderBill,
      },
    })
  }

  const billsDownloadHandler = (order: number, uwi?: string, file?: string): void => {
    downloadHandler(order, uwi, file, true)
  }

  const isLoading = getOrderLoading || downloadTokenLoading

  const primaryData = (getWellIdentifiersOnOrders() ?? []).filter((wellOnOrder: PrimaryTableType) =>
    ArrayUtils.isNotEmpty(wellOnOrder?.WellIdentifier?.File)
  )

  const orderNumber = getOrderNumber() ?? 0
  const isShowData = !getOrderLoading && isNoData == null && primaryData.length > 0

  return (
    <Spin spinning={isLoading} tip="Loading ..." size="large">
      <DownloadWrapper>
        {contextHolder}
        <Title level={2} style={{ textAlign: 'center', margin: '0 0 1.5rem' }}>
          Download {missingEToursOnly === 'true' ? 'Missing Tours' : 'Tours'} Reports
          {orderNumber > 0 && ` - Order #${orderNumber}`}
        </Title>
        {isNoData != null && <DownloadError missingEToursOnly={missingEToursOnly} msg={isNoData} />}
        {isShowData && (
          <div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                margin: '0 0 1rem 0',
              }}
            >
              <Button type="primary" onClick={() => downloadHandler(orderNumber)}>
                Download All
              </Button>
            </div>
            {missingEToursOnly === 'false' && (
              <div>
                <Title level={4} style={{ margin: '1.0rem 0 0.5rem' }}>
                  Bills &amp; Invoices
                </Title>
                <DownloadSecondaryTable
                  order={orderNumber}
                  uwi="x"
                  data={getBills()}
                  downloadHandler={billsDownloadHandler}
                  isLookPrimary={true}
                />
              </div>
            )}
            <div>
              <Title level={4} style={{ margin: '1.5rem 0 0.5rem' }}>
                Tours
              </Title>
              <DownloadPrimaryTable
                order={orderNumber}
                data={primaryData}
                downloadHandler={downloadHandler}
              />
            </div>
          </div>
        )}
      </DownloadWrapper>
    </Spin>
  )
}

export default DownloadETours
